import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MessageService, ConfirmationService } from 'primeng/api';

import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';

import { ApiService, Company, Supplier } from '../../core/api.service';

@Component({
    selector: 'app-suppliers',
    standalone: true,
    imports: [
        CommonModule, FormsModule,
        TableModule, ButtonModule, InputTextModule, SelectModule, DialogModule, ToastModule, ConfirmDialogModule, TagModule
    ],
    providers: [MessageService, ConfirmationService],
    template: `
        <p-toast></p-toast>
        <p-confirmDialog></p-confirmDialog>

        <div class="card">
            <div class="flex justify-between items-center mb-4">
                <div class="font-semibold text-xl"><i class="pi pi-briefcase mr-2"></i>Suppliers</div>
                <p-button label="Add Supplier" icon="pi pi-plus" (onClick)="openNew()"></p-button>
            </div>

            <p-table [value]="suppliers" [loading]="loading" [paginator]="true" [rows]="10"
                styleClass="p-datatable-sm p-datatable-striped">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Name</th>
                        <th>Company</th>
                        <th>NTN</th>
                        <th>STRN</th>
                        <th>Address</th>
                        <th style="width:8rem">Status</th>
                        <th style="width:11rem">Actions</th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-s>
                    <tr>
                        <td>{{ s.name }}</td>
                        <td>{{ companyName(s.companyId) }}</td>
                        <td>{{ s.ntn }}</td>
                        <td>{{ s.strn }}</td>
                        <td>{{ s.address }}</td>
                        <td><p-tag [value]="s.isActive ? 'Active' : 'Inactive'" [severity]="s.isActive ? 'success' : 'secondary'"></p-tag></td>
                        <td>
                            <p-button icon="pi pi-pencil" severity="info" [text]="true" (onClick)="openEdit(s)"></p-button>
                            <p-button *ngIf="s.isActive" icon="pi pi-ban" severity="danger" [text]="true" (onClick)="confirmDelete(s)" pTooltip="Deactivate"></p-button>
                            <p-button *ngIf="!s.isActive" icon="pi pi-check-circle" severity="success" [text]="true" (onClick)="activate(s)" pTooltip="Activate"></p-button>
                        </td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr><td colspan="7" class="text-center p-6 text-muted-color">No suppliers. Add one.</td></tr>
                </ng-template>
            </p-table>
        </div>

        <p-dialog [header]="editId ? 'Edit Supplier' : 'Add Supplier'" [(visible)]="dialog" [modal]="true" [style]="{ width: '500px' }" [draggable]="false">
            <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-2">
                    <label class="text-sm font-medium">Company *</label>
                    <p-select [options]="companies" [(ngModel)]="item.companyId" optionLabel="name" optionValue="id" placeholder="Select company" [filter]="true" styleClass="w-full"></p-select>
                </div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Name *</label><input pInputText [(ngModel)]="item.name" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">NTN</label><input pInputText [(ngModel)]="item.ntn" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">STRN</label><input pInputText [(ngModel)]="item.strn" /></div>
                <div class="flex flex-col gap-2"><label class="text-sm font-medium">Address</label><input pInputText [(ngModel)]="item.address" /></div>
            </div>
            <ng-template pTemplate="footer">
                <p-button label="Cancel" severity="secondary" [text]="true" (onClick)="dialog = false"></p-button>
                <p-button label="Save" icon="pi pi-check" (onClick)="save()"></p-button>
            </ng-template>
        </p-dialog>
    `
})
export class Suppliers implements OnInit {
    suppliers: Supplier[] = [];
    companies: Company[] = [];
    loading = false;

    dialog = false;
    editId: number | null = null;
    item: Supplier = {} as Supplier;

    constructor(
        private api: ApiService,
        private messageService: MessageService,
        private confirmationService: ConfirmationService,
        private cd: ChangeDetectorRef
    ) {}

    ngOnInit(): void { this.loadCompanies(); this.load(); }

    loadCompanies(): void {
        this.api.getCompanies().subscribe({
            next: (data) => { this.companies = data; this.cd.detectChanges(); },
            error: () => {}
        });
    }

    companyName(id: number): string { return this.companies.find((c) => c.id === id)?.name || '-'; }

    load(): void {
        this.loading = true;
        this.api.getSuppliers().subscribe({
            next: (data) => { this.suppliers = data; this.loading = false; this.cd.detectChanges(); },
            error: () => { this.loading = false; this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load suppliers.' }); this.cd.detectChanges(); }
        });
    }

    openNew(): void { this.editId = null; this.item = {} as Supplier; this.dialog = true; }
    openEdit(s: Supplier): void { this.editId = s.id; this.item = { ...s }; this.dialog = true; }

    save(): void {
        if (!this.item.companyId) { this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Please select a company.' }); return; }
        if (!this.item.name || !this.item.name.trim()) { this.messageService.add({ severity: 'warn', summary: 'Wait', detail: 'Supplier name is required.' }); return; }

        const obs = this.editId ? this.api.updateSupplier(this.editId, this.item) : this.api.createSupplier(this.item);
        obs.subscribe({
            next: () => { this.dialog = false; this.messageService.add({ severity: 'success', summary: 'Saved', detail: this.editId ? 'Supplier updated.' : 'Supplier created.' }); this.load(); this.cd.detectChanges(); },
            error: (err) => { this.messageService.add({ severity: 'error', summary: 'Error', detail: err?.error?.message || 'Save failed.' }); this.cd.detectChanges(); }
        });
    }

    confirmDelete(s: Supplier): void {
        this.confirmationService.confirm({
            message: `Deactivate "${s.name}"?`, header: 'Confirm Deactivate', icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, deactivate', rejectLabel: 'Cancel', acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.api.deleteSupplier(s.id).subscribe({
                    next: (res: any) => { this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Deactivated.' }); this.load(); this.cd.detectChanges(); },
                    error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed.' }); this.cd.detectChanges(); }
                });
            }
        });
    }

    activate(s: Supplier): void {
        this.api.activateSupplier(s.id).subscribe({
            next: (res: any) => { this.messageService.add({ severity: 'success', summary: 'Done', detail: res?.message || 'Activated.' }); this.load(); this.cd.detectChanges(); },
            error: () => { this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Activate failed.' }); this.cd.detectChanges(); }
        });
    }
}
